import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LoginPage } from '@/pages/Login';
import { DashboardPage } from '@/pages/Dashboard';
import { CategoriaPage } from '@/pages/CategoriaPage';
import { ItemDetailPage } from '@/pages/ItemDetailPage';
import { VarredurasPage } from '@/pages/VarredurasPage';
import { ConfiguracoesPage } from '@/pages/ConfiguracoesPage';
import { AuditoriaPage } from '@/pages/AuditoriaPage';
import { TokensPage } from '@/pages/TokensPage';
import { ObsidianPage } from '@/pages/ObsidianPage';
import { CursosPage } from '@/pages/CursosPage';
import { IntegracoesPage } from '@/pages/IntegracoesPage';
import { GlossarioPage } from '@/pages/GlossarioPage';
import { LousasPage } from '@/pages/LousasPage';
import { Shell } from '@/components/Shell';
import { ToastProvider } from '@/lib/toast';
import { useAuthStore } from '@/stores/auth';
import { api, ApiError } from '@/lib/api';
import type { MeResponse } from '@central/types';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

function ProtectedRoute({ children }: { children: JSX.Element }): JSX.Element {
  const accessToken = useAuthStore((s) => s.accessToken);
  if (!accessToken) return <Navigate to="/login" replace />;
  return children;
}

function Placeholder({ title }: { title: string }): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center">
      <h1 className="text-2xl font-semibold mb-2">{title}</h1>
      <p className="text-sm text-muted-foreground max-w-md">
        Esta aba será construída nas próximas semanas do roadmap. Este é o shell da Semana 1.
      </p>
    </div>
  );
}

export function App(): JSX.Element {
  const setUser = useAuthStore((s) => s.setUser);
  const accessToken = useAuthStore((s) => s.accessToken);
  const clearAuth = useAuthStore((s) => s.clearAuth);

  useEffect(() => {
    if (!accessToken) return;
    api<MeResponse>('/api/auth/me')
      .then(setUser)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) clearAuth();
      });
  }, [accessToken, setUser, clearAuth]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Shell />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="auditoria" element={<AuditoriaPage />} />
            <Route path="categoria/:tipo" element={<CategoriaPage />} />
            <Route path="items/:id" element={<ItemDetailPage />} />
            <Route path="busca" element={<Placeholder title="Busca" />} />
            <Route path="obsidian" element={<ObsidianPage />} />
            <Route path="cursos" element={<CursosPage />} />
            <Route path="glossario" element={<GlossarioPage />} />
            <Route path="lousas" element={<LousasPage />} />
            <Route path="integracoes" element={<IntegracoesPage />} />
            <Route path="configuracoes" element={<ConfiguracoesPage />} />
            <Route path="configuracoes/varreduras" element={<VarredurasPage />} />
            <Route path="configuracoes/tokens" element={<TokensPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
